import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  Chip,
  CircularProgress,
  IconButton,
  Collapse,
  Divider
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import callService from '../services/CallService';

export default function CallHistory({ serviceId, contactId, phoneNumber, refreshKey = 0 }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  
  const getDirectionIcon = (direction, status, callType) => {
    const isMissed = status === 'MISSED' || callType === 'missed';
    const arrowColor = isMissed ? '#f44336' : '#4caf50';
    
    if (direction === 'BUSINESS_INITIATED' || callType === 'outgoing') {
      return <CallMadeIcon sx={{ fontSize: 18, color: arrowColor }} />;
    } else if (direction === 'USER_INITIATED' || callType === 'incoming') {
      return <CallReceivedIcon sx={{ fontSize: 18, color: arrowColor }} />;
    }
    return <CallIcon sx={{ fontSize: 18 }} />;
  };

  const fetchCallHistory = async () => {
    if (!serviceId || !contactId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const historyData = await callService.getCallHistory(serviceId, contactId, 50);
      setCalls(Array.isArray(historyData) ? historyData : []);
    } catch (err) {
      console.error('Failed to fetch call history:', err);
      setError(err.message);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contactId) {
      fetchCallHistory();
    }
  }, [contactId, serviceId, refreshKey]);

  const toggleOpen = () => setOpen(!open);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Box 
        onClick={toggleOpen}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1,
          borderTop: '1px solid #eef2f7',
          borderBottom: '1px solid #eef2f7',
          bgcolor: '#f8fafc',
          px: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CallIcon sx={{ fontSize: 20, color: '#64748b' }} />
          <Typography variant="body2" fontWeight={600} color= "#64748b">
            Call History
          </Typography>
        </Box>
        <IconButton size="small">
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>

      {/* Content */}
      <Collapse in={open}>
        <Box sx={{ mt: 1, maxHeight: 300, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Typography variant="caption" color="error" sx={{ p: 1, display: 'block' }}>
              Failed to load call history
            </Typography>
          ) : calls.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
              No call history available
            </Typography>
          ) : (
            <List dense disablePadding>
              {calls.map((call, index) => {
                const callStatus = call.call_status || call.status;
                const callType = call.call_type;
                const channelLabel = call.call_channel === 'phone' ? 'Phone' : 'WhatsApp';
                return (
                  <React.Fragment key={call.call_id || index}>
                    <ListItem sx={{ px: 1, py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        
                        {/* Direction Arrow Only */}
                        <Box sx={{ minWidth: 28, textAlign: 'center' }}>
                          {getDirectionIcon(call.direction, callStatus, callType)}
                        </Box>
                        
                        {/* Call Details - No direction text */}
                        <Box sx={{ flex: 1 }}>
                          
                          {/* Date and Duration */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={channelLabel} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                            <AccessTimeIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(call.started_at || call.initiated_at || call.created_at)}
                            </Typography>
                            {call.duration > 0 && (
                              <>
                                <Typography variant="caption" color="text.secondary">•</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Duration: {formatDuration(call.duration)}
                                </Typography>
                              </>
                            )}
                            {callType && (
                              <>
                                <Typography variant="caption" color="text.secondary">•</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                  {callType}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    {index < calls.length - 1 && <Divider sx={{ my: 0.5 }} />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}